import { Check, Edit, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Form/Input';
import { Textarea } from '@/components/UI/Form/TextArea';
import HookRichTextEditor from '@/components/UI/HookForm/Form/RichTextEditor';
import SafeHtml from '@/components/UI/SafeHtml';

interface EditableFieldProps {
  name: string;
  label: string;
  value: string | number | boolean;
  type?: 'text' | 'number' | 'textarea' | 'richtext' | 'checkbox';
  onSave: (name: string, value: string | number | boolean) => Promise<void>;
}

const EditableField: React.FC<EditableFieldProps> = ({ name, label, value: initialValue, type = 'text', onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm({ defaultValues: { [name]: initialValue } });

  // Sync internal state with external value changes
  useEffect(() => {
    setValue(initialValue);
    methods.reset({ [name]: initialValue });
  }, [initialValue, name, methods]);

  const handleSave = async (data: { [x: string]: string | number | boolean }) => {
    setIsLoading(true);
    try {
      await onSave(name, data[name]);
      // Update local state with the new value
      setValue(data[name]);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save', error);
      // Revert value on failure
      setValue(initialValue);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  return (
    <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
      <dt className='text-sm font-medium text-gray-500'>{label}</dt>
      <dd className='mt-1 flex items-center justify-between text-sm text-gray-900 sm:col-span-2 sm:mt-0'>
        {isEditing ? (
          <div className='flex w-full flex-col gap-2'>
            {type === 'richtext' ? (
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSave)} className='w-full'>
                  <HookRichTextEditor name={name} height='300px' />
                  <div className='mt-2 flex items-center justify-end gap-1'>
                    <Button btnStyle='primary' type='submit' disabled={isLoading} customClassName='p-2' icon={<Check size={16} />} />
                    <Button btnStyle='secondary' onClick={handleCancel} customClassName='p-2' icon={<X size={16} />} />
                  </div>
                </form>
              </FormProvider>
            ) : type === 'textarea' ? (
              <>
                <Textarea
                  value={value.toString()}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
                  className='w-full'
                  rows={5}
                  style={{ minHeight: '120px' }}
                />
                <div className='flex items-center justify-end gap-1'>
                  <Button
                    btnStyle='primary'
                    onClick={async () => {
                      setIsLoading(true);
                      try {
                        await onSave(name, value);
                        setIsEditing(false);
                      } catch (error) {
                        console.error('Failed to save', error);
                        // Revert value on failure
                        setValue(initialValue);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    customClassName='p-2'
                    icon={<Check size={16} />}
                  />
                  <Button btnStyle='secondary' onClick={handleCancel} customClassName='p-2' icon={<X size={16} />} />
                </div>
              </>
            ) : type === 'checkbox' ? (
              <div className='flex w-full items-center gap-2'>
                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={value === true}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.checked)}
                    className='h-4 w-4 text-primaryColor-normal focus:ring-primaryColor-normal border-gray-300 rounded'
                  />
                  <span className='ml-2 text-sm text-gray-700'>{value === true ? 'Active' : 'Inactive'}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <Button
                    btnStyle='primary'
                    onClick={async () => {
                      setIsLoading(true);
                      try {
                        await onSave(name, value);
                        setIsEditing(false);
                      } catch (error) {
                        console.error('Failed to save', error);
                        // Revert value on failure
                        setValue(initialValue);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    customClassName='p-2'
                    icon={<Check size={16} />}
                  />
                  <Button btnStyle='secondary' onClick={handleCancel} customClassName='p-2' icon={<X size={16} />} />
                </div>
              </div>
            ) : (
              <div className='flex w-full items-center gap-2'>
                <Input
                  type={type}
                  value={value?.toString() ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
                  className='w-full'
                />
                <div className='flex items-center gap-1'>
                  <Button
                    btnStyle='primary'
                    onClick={async () => {
                      setIsLoading(true);
                      try {
                        await onSave(name, value);
                        setIsEditing(false);
                      } catch (error) {
                        console.error('Failed to save', error);
                        // Revert value on failure
                        setValue(initialValue);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    customClassName='p-2'
                    icon={<Check size={16} />}
                  />
                  <Button btnStyle='secondary' onClick={handleCancel} customClassName='p-2' icon={<X size={16} />} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {type === 'richtext' ? (
              <SafeHtml html={value.toString()} />
            ) : type === 'checkbox' ? (
              <div className='flex items-center'>
                <input
                  type='checkbox'
                  checked={value === true}
                  disabled
                  className='h-4 w-4 text-primaryColor-normal focus:ring-primaryColor-normal border-gray-300 rounded'
                />
                <span className='ml-2 text-sm text-gray-700'>{value === true ? 'Active' : 'Inactive'}</span>
              </div>
            ) : (
              <span>{value}</span>
            )}
            <Button btnStyle='ghost' onClick={() => setIsEditing(true)} customClassName='p-1' icon={<Edit size={16} />} />
          </>
        )}
      </dd>
    </div>
  );
};

export default EditableField;
